/**
 * GlobeViewer scene/lifecycle mixin: starfield, globe-data load, grid lines,
 * lights, color transitions, tooltip, resize, animation loop, and play/pause.
 * Methods run with `this` bound to a GlobeViewer instance; composed onto the
 * prototype in globe-viewer.ts. Extracted verbatim (Phase 2, PR-C). @ts-nocheck.
 */
/* eslint-disable */
// @ts-nocheck
import * as THREE from 'three'
import { LineMaterial } from 'three/addons/lines/LineMaterial.js'
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js'
import { geoToCartesian } from './geo-helpers'

export const sceneMethods = {
      createStarfield(globeGroup) {
        const starGeometry = new THREE.BufferGeometry();
        const starCount = 600;
        const positions = new Float32Array(starCount * 3);
        const colors = new Float32Array(starCount * 3);

        // Pastel color palette for stars (more saturated for visibility)
        const pastelPalette = [
          [1.0, 0.6, 0.7],   // pink
          [0.6, 0.7, 1.0],   // blue
          [0.6, 1.0, 0.7],   // mint
          [1.0, 0.8, 0.5],   // peach/orange
          [0.8, 0.6, 1.0],   // lavender
          [0.5, 1.0, 0.9],   // cyan
          [1.0, 1.0, 0.6],   // yellow
        ];

        for (let i = 0; i < starCount; i++) {
          const radius = 8 + Math.random() * 15;
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.acos((Math.random() * 2) - 1);

          positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
          positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
          positions[i * 3 + 2] = radius * Math.cos(phi);

          // Pick a random color from palette
          const color = pastelPalette[Math.floor(Math.random() * pastelPalette.length)];
          colors[i * 3] = color[0];
          colors[i * 3 + 1] = color[1];
          colors[i * 3 + 2] = color[2];
        }

        starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        starGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        const starMaterial = new THREE.PointsMaterial({
          size: 0.1,
          vertexColors: true,
          transparent: true,
          // Start hidden; playIntro fades the stars in (resting opacity 0.9).
          opacity: 0,
          sizeAttenuation: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });

        const stars = new THREE.Points(starGeometry, starMaterial);
        globeGroup.add(stars);
        this.starsMaterial = starMaterial;
        this.STAR_OPACITY = 0.9;
      }

,
      // Yield to the browser for one frame so a long synchronous pass becomes a
      // sequence of short tasks (keeps the main thread responsive during build).
      yieldFrame() {
        return new Promise((resolve) => requestAnimationFrame(() => resolve()));
      }

,
      // Build a feature layer (fills or borders) in batches of BUILD_BATCH,
      // yielding between batches so no single frame pays for the whole layer.
      // `entries` is the baked render-bundle array (countries or states).
      async buildLayerChunked(builder, entries, type) {
        const total = entries.length;
        const batch = this.BUILD_BATCH || 8;
        for (let start = 0; start < total; start += batch) {
          builder.call(this, entries, type, { start, count: batch });
          await this.yieldFrame();
        }
      }

,
      async loadGlobeData() {
        try {
          // Prefer the fetch kicked off at module load (overlaps the scramble
          // intro); fall back to fetching here if it is missing or rejected.
          let data = window.__globeDataPromise ? await window.__globeDataPromise : null;
          if (!data) {
            const [dotsData, regionRender] = await Promise.all([
              fetch('/data/dots-330.json').then(r => r.json()),
              fetch('/data/region-render.json').then(r => r.json()),
            ]);
            data = { dotsData, regionRender };
          }
          const { dotsData, regionRender } = data;

          // Self-contained per-region render bundle (code, name, centroid, fill
          // caps, border lines), precomputed by scripts/generate-region-render.ts.
          // The client iterates these arrays directly — no raw GeoJSON, no runtime
          // tessellation/densification, and no cross-file index coupling.
          //
          // No-fallback trade-off: this baked file is the ONLY source of region
          // geometry — the conic builder was intentionally dropped from the client
          // bundle. If this fetch failed (caught below), the globe renders an empty
          // sphere with no regions. That is an accepted trade-off in exchange for
          // not shipping the conic library (and raw GeoJSON) to clients.
          this.regionRender = regionRender;

          const setStatus = (text) => {
            if (typeof window.__setGlobeLoadingStatus === 'function') {
              window.__setGlobeLoadingStatus(text);
            }
          };
          const isParticle = this.getCurrentDataType() === 'digital_ids';

          // Step A: base globe + lights (cheap).
          setStatus('LOADING GEOMETRY');
          this.createGlobeFill();
          this.createLights();
          await this.yieldFrame();

          // Step B: region fills, batched (upload baked conic cap buffers).
          await this.buildLayerChunked(this.createRegionFills, regionRender.countries, 'country');
          await this.buildLayerChunked(this.createRegionFills, regionRender.states, 'state');

          // Step C: borders, batched (upload baked line positions).
          setStatus('RENDERING BORDERS');
          await this.buildLayerChunked(this.createBorders, regionRender.countries, 'country');
          await this.buildLayerChunked(this.createBorders, regionRender.states, 'state');
          this.linkFillsToBorders();
          this.applyRegionDefaultStyles();
          this.applyModeColors();
          this.bordersReady = true;
          await this.yieldFrame();

          // Step D: graticule.
          setStatus('TRACING GRATICULE');
          this.createGridLines();
          await this.yieldFrame();

          // Step E: dots.
          this.createDotsFromPrecomputed(dotsData.positions);

          // Warm-render BOTH custom shaders while their reveals are at 0 (so the
          // compile lands on an invisible frame) regardless of starting mode —
          // this also de-hitches the first later mode switch.
          if (this.meshes.gridLines) this.meshes.gridLines.visible = true;
          if (this.meshes.dots) this.meshes.dots.visible = true;
          this.renderer.render(this.scene, this.camera);

          setStatus(isParticle ? 'MAPPING COVERAGE' : 'MAPPING JURISDICTIONS');

          // Hand off to the choreographed reveal.
          this.playIntro();

          // Repeat visits in this session get the fast path next time.
          try { sessionStorage.setItem('atlas-intro-seen', '1'); } catch (e) {}

        } catch (error) {
          console.error('Error loading globe data:', error);
          this.revealChrome();
          if (this.renderer && this.renderer.domElement) {
            this.renderer.domElement.style.opacity = '1';
          }
          this.hideLoadingOverlay();
        }
      }

,
      // Reveal the page chrome (header / filter / keyboard hint). Safe to call
      // more than once; also the error/fallback path so chrome never gets stuck
      // hidden behind the intro start state.
      revealChrome() {
        if (typeof document !== 'undefined' && document.body) {
          document.body.classList.add('globe-intro-done');
        }
      }

,
      // Choreographed entrance. Mode-agnostic: per-mode specifics (grid trace vs
      // dot stagger, globe-fill opacity) are owned by applyGlobeStyle(type, ...).
      playIntro() {
        const type = this.getCurrentDataType();
        const isParticle = type === 'digital_ids';
        const fast = this.introFast;

        // Stage 0: reveal the chrome + canvas. Stars fade in; the canvas fades
        // from the hidden state set in init().
        this.revealChrome();
        if (this.renderer && this.renderer.domElement) {
          this.renderer.domElement.style.opacity = '1';
        }

        const sphereTarget = isParticle
          ? this.STYLE_PARTICLE.globeFillOpacity
          : this.STYLE_DEFAULTS.globeFillOpacity;

        if (fast) {
          // Fade-only path (reduced motion or repeat visit): snap everything.
          if (this.starsMaterial) this.starsMaterial.opacity = this.STAR_OPACITY;
          if (this.meshes.globeFill) this.meshes.globeFill.material.opacity = sphereTarget;
          this.applyGlobeStyle(type, false);
          this.hideLoadingOverlay();
          this.needsRender = true;
          return;
        }

        // Stars fade in.
        if (this.starsMaterial) {
          this.startStyleTween('introStars', 0, this.STAR_OPACITY, (v) => {
            this.starsMaterial.opacity = v;
          }, { duration: this.INTRO_FADE_DURATION });
        }

        // Camera dolly: gentle push-in from farther out, spanning the cascade.
        const restingZoom = this.zoom;
        this.zoom = restingZoom * this.INTRO_DOLLY_FACTOR;
        this.startStyleTween('introDolly', this.zoom, restingZoom, (v) => {
          this.zoom = v;
        }, { duration: this.INTRO_DOLLY_DURATION });

        // Sphere is snapped to its mode-target opacity (the canvas fade provides
        // the visual fade-in). Keeping it at target means applyGlobeStyle's own
        // globeFillOpacity tween in the hero reveal is a from===to no-op rather
        // than fighting a concurrent sphere tween.
        if (this.meshes.globeFill) {
          this.meshes.globeFill.material.opacity = sphereTarget;
          this.meshes.globeFill.material.needsUpdate = true;
        }

        // Light bloom: key + ambient ramp up from black so the globe lights up.
        const keyTarget = this.meshes.keyLight ? this.meshes.keyLight.intensity : 0;
        const ambientTarget = this.meshes.ambientLight ? this.meshes.ambientLight.intensity : 0;
        if (this.meshes.keyLight) {
          this.meshes.keyLight.intensity = 0;
          this.startStyleTween('introKeyLight', 0, keyTarget, (v) => {
            this.meshes.keyLight.intensity = v;
          }, { duration: this.INTRO_FADE_DURATION });
        }
        if (this.meshes.ambientLight) {
          this.meshes.ambientLight.intensity = 0;
          this.startStyleTween('introAmbient', 0, ambientTarget, (v) => {
            this.meshes.ambientLight.intensity = v;
          }, { duration: this.INTRO_FADE_DURATION });
        }

        // Stage 3 (hero reveal): grid trace (Age) or dot stagger (Digital IDs),
        // a beat after the sphere/world so it reads as the climax.
        setTimeout(() => {
          this.applyGlobeStyle(type, true);
          // Closer: ping active jurisdictions once the hero reveal is underway.
          const revealMs = isParticle ? this.DOT_REVEAL_DURATION : this.GRID_REVEAL_DURATION;
          setTimeout(() => this.pingActiveRegions(), Math.round(revealMs * 0.55));
        }, this.INTRO_STAGE_DELAY * 2);

        // The entrance above starts immediately and plays underneath the loading
        // pill; defer hiding the overlay until the status sequence has held its
        // final milestone for its minimum dwell, then cross-fade it out.
        if (typeof window.__whenGlobeLoadingStatusSettled === 'function') {
          window.__whenGlobeLoadingStatusSettled(() => this.hideLoadingOverlay());
        } else {
          this.hideLoadingOverlay();
        }
        this.needsRender = true;
      }

,
      // One-shot brighten sweeping across the active jurisdictions (west -> east)
      // as the intro closer. Active borders are those colored ACTIVE_BORDER_COLOR
      // after applyModeColors, so this is mode-correct for free.
      pingActiveRegions() {
        if (this.introFast) return;
        const active = [];
        const collect = (list) => {
          if (!list) return;
          list.forEach((mesh) => {
            if (mesh && mesh.material && mesh.material.color &&
                mesh.material.color.getHex() === this.ACTIVE_BORDER_COLOR &&
                mesh.material.opacity > 0) {
              active.push(mesh);
            }
          });
        };
        collect(this.meshes.countryBorders);
        collect(this.meshes.stateBorders);
        if (active.length === 0) return;

        // Sweep west -> east by centroid longitude.
        active.sort((a, b) => {
          const la = (a.userData.centroid && a.userData.centroid[0]) || 0;
          const lb = (b.userData.centroid && b.userData.centroid[0]) || 0;
          return la - lb;
        });

        active.forEach((mesh, i) => {
          setTimeout(() => {
            // Brighten toward the hover tint, then settle back to active blue.
            this.startTransition(mesh, this.HOVER_BORDER_COLOR, this.PING_DURATION * 0.4);
            setTimeout(() => {
              this.startTransition(mesh, this.ACTIVE_BORDER_COLOR, this.PING_DURATION * 0.6);
            }, this.PING_DURATION * 0.4);
          }, i * this.PING_STAGGER);
        });
      }

,
      createGridLines() {
        const lines = [];
        const progress = []; // per-vertex draw-on order along its own axis: 0 = first, 1 = last
        const axis = []; // 0 = latitude ring (sweeps top->down), 1 = longitude meridian (sweeps left->right)
        const count = 12;
        const step = 180 / count;
        // Latitude rings reveal north -> south; meridians reveal west -> east.
        const latProgress = (lat) => (90 - lat) / 180;
        const lonProgress = (lon) => (lon + 180) / 360;

        // Generate latitude lines (axis 0, top-down)
        for (let lat = -90; lat <= 90; lat += step) {
          const p = latProgress(lat);
          for (let lon = -180; lon < 180; lon += 5) {
            const lon2 = lon + 5;
            const [x1, y1, z1] = geoToCartesian(lon, lat, 1.08);
            const [x2, y2, z2] = geoToCartesian(lon2, lat, 1.08);
            lines.push(x1, y1, z1, x2, y2, z2);
            progress.push(p, p);
            axis.push(0, 0);
          }
        }

        // Generate longitude lines (axis 1, left-to-right)
        for (let lon = -180; lon <= 180; lon += step) {
          const p = lonProgress(lon);
          for (let lat = -90; lat < 90; lat += 5) {
            const lat2 = lat + 5;
            const [x1, y1, z1] = geoToCartesian(lon, lat, 1.08);
            const [x2, y2, z2] = geoToCartesian(lon, lat2, 1.08);
            lines.push(x1, y1, z1, x2, y2, z2);
            progress.push(p, p);
            axis.push(1, 1);
          }
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(lines, 3));
        geometry.setAttribute('aProgress', new THREE.Float32BufferAttribute(progress, 1));
        geometry.setAttribute('aAxis', new THREE.Float32BufferAttribute(axis, 1));

        // Custom line shader supporting an independent draw-on reveal per axis:
        // latitude rings and longitude meridians each have their own reveal uniform,
        // so they trace in different directions. A brief brightening rides the
        // leading edge while a reveal is in progress.
        const material = new THREE.ShaderMaterial({
          uniforms: {
            uColor: { value: new THREE.Color(0x525252) },
            uOpacity: { value: 0.5 },
            uRevealLat: { value: 0.0 }, // latitude rings; applyGlobeStyle plays 0 -> 1
            uRevealLon: { value: 0.0 }, // longitude meridians; applyGlobeStyle plays 0 -> 1
            uEdge: { value: 0.12 },
          },
          vertexShader: `
            attribute float aProgress;
            attribute float aAxis;
            varying float vProgress;
            varying float vAxis;
            void main() {
              vProgress = aProgress;
              vAxis = aAxis;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `,
          fragmentShader: `
            uniform vec3 uColor;
            uniform float uOpacity;
            uniform float uRevealLat;
            uniform float uRevealLon;
            uniform float uEdge;
            varying float vProgress;
            varying float vAxis;
            void main() {
              float reveal = (vAxis < 0.5) ? uRevealLat : uRevealLon;
              if (vProgress > reveal) discard;
              // Glow only rides the leading edge while the reveal is in progress.
              float animating = 1.0 - step(1.0, reveal);
              float edge = smoothstep(reveal - uEdge, reveal, vProgress) * animating;
              vec3 col = mix(uColor, vec3(1.0), edge * 0.6);
              gl_FragColor = vec4(col, uOpacity);
            }
          `,
          transparent: true,
        });

        const mesh = new THREE.LineSegments(geometry, material);
        this.globeGroup.add(mesh);
        this.meshes.gridLines = mesh;
      }

,
      createLights() {
        // Key light (main light source)
        const keyLight = new THREE.DirectionalLight(0xffffff, 5.0);
        keyLight.position.set(this.keyLightX, this.keyLightY, this.keyLightZ);
        this.scene.add(keyLight);
        this.meshes.keyLight = keyLight;

        // Fill light (softens shadows)
        const fillLight = new THREE.DirectionalLight(0xE5EDFF, 0.8);
        fillLight.position.set(-5, 0, -5);
        this.scene.add(fillLight);
        this.meshes.fillLight = fillLight;

        // Rim light (edge highlight)
        const rimLight = new THREE.DirectionalLight(0xcfff4d, 1.0);
        rimLight.position.set(0, 0, -5);
        this.scene.add(rimLight);
        this.meshes.rimLight = rimLight;

        // Ambient light
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
        this.scene.add(ambientLight);
        this.meshes.ambientLight = ambientLight;
      }
      
,
      startTransition(mesh, targetColor, duration = this.TRANSITION_DURATION) {
        const currentColor = mesh.material.color.clone();
        
        this.transitions.set(mesh, {
          startColor: currentColor,
          targetColor: new THREE.Color(targetColor),
          startTime: Date.now(),
          duration: duration
        });
        
        this.needsRender = true;
      }

      // Start an animated tween of a numeric property (opacity, dot size, etc.).
      // Keyed so a new switch mid-animation replaces the in-flight tween.
,
      startStyleTween(key, from, to, apply, options = {}) {
        const duration = options.duration != null ? options.duration : this.STYLE_TRANSITION_DURATION;
        if (from === to) {
          apply(to);
          if (options.onComplete) options.onComplete();
          this.styleTweens.delete(key);
          return;
        }
        this.styleTweens.set(key, {
          from,
          to,
          apply,
          onComplete: options.onComplete || null,
          startTime: Date.now(),
          duration,
        });
        this.needsRender = true;
      }

      // Process all active style tweens (called in animate loop)
,
      updateStyleTweens() {
        if (this.styleTweens.size === 0) return;

        const now = Date.now();
        const completed = [];

        this.styleTweens.forEach((tween, key) => {
          const elapsed = now - tween.startTime;
          const progress = Math.min(elapsed / tween.duration, 1);

          // Ease-out cubic — matches updateTransitions for a consistent feel
          const eased = 1 - Math.pow(1 - progress, 3);
          const value = tween.from + (tween.to - tween.from) * eased;
          tween.apply(value);

          if (progress >= 1) completed.push(key);
          this.needsRender = true;
        });

        completed.forEach(key => {
          const tween = this.styleTweens.get(key);
          if (tween && tween.onComplete) tween.onComplete();
          this.styleTweens.delete(key);
        });
      }

      // Switch the globe's visual style based on the active data type. Digital IDs
      // gets the 'particle' look (dotted continents up, solid sphere + grid lines
      // down); Age Assurance restores the default look. Region borders/fills stay
      // driven by applyModeColors, so highlighting and interactivity are unchanged.
,
      applyGlobeStyle(dataType, animate = true) {
        const type = dataType || this.getCurrentDataType();
        const isParticle = type === 'digital_ids';
        const target = isParticle ? this.STYLE_PARTICLE : this.STYLE_DEFAULTS;
        this.currentGlobeStyle = isParticle ? 'particle' : 'default';

        const globeFill = this.meshes.globeFill;
        const dots = this.meshes.dots;
        const gridLines = this.meshes.gridLines;

        // Grid lines: subtly dim and descend in Digital IDs, brighten and rise back
        // in Age Assurance — synced with a draw-on reveal when entering Age Assurance
        // (and on initial load). Latitude rings (top-down) and longitude meridians
        // (left-to-right) reveal independently.
        if (gridLines && gridLines.material && gridLines.material.uniforms) {
          gridLines.visible = true;
          const u = gridLines.material.uniforms;
          const opacityFrom = u.uOpacity.value;
          const opacityTo = target.gridLinesOpacity;
          const scaleFrom = gridLines.scale.x;
          const scaleTo = target.gridLinesScale;
          const onComplete = () => {
            if (opacityTo === 0) gridLines.visible = false;
          };
          if (animate) {
            this.startStyleTween('gridLinesOpacity', opacityFrom, opacityTo, (v) => { u.uOpacity.value = v; }, { onComplete });
            this.startStyleTween('gridLinesScale', scaleFrom, scaleTo, (v) => { gridLines.scale.setScalar(v); });
          } else {
            u.uOpacity.value = opacityTo;
            gridLines.scale.setScalar(scaleTo);
            onComplete();
          }

          // Draw-on reveal: replay the trace entering Age Assurance (and load); snap
          // to fully drawn for Digital IDs so the subtle dim/descend reads as one.
          if (isParticle || !animate) {
            u.uRevealLat.value = 1.0;
            u.uRevealLon.value = 1.0;
          } else {
            u.uRevealLat.value = 0.0;
            u.uRevealLon.value = 0.0;
            this.startStyleTween('gridRevealLat', 0, 1, (v) => { u.uRevealLat.value = v; }, { duration: this.GRID_REVEAL_DURATION });
            this.startStyleTween('gridRevealLon', 0, 1, (v) => { u.uRevealLon.value = v; }, { duration: this.GRID_REVEAL_DURATION });
          }
        }

        // Solid sphere: recede in particle mode but stay visible so it still
        // writes depth and occludes far-side dots (and hover occlusion testing).
        if (globeFill && globeFill.material) {
          const from = globeFill.material.opacity;
          const to = target.globeFillOpacity;
          const apply = (v) => {
            globeFill.material.opacity = v;
            globeFill.material.needsUpdate = true;
          };
          if (animate) this.startStyleTween('globeFillOpacity', from, to, apply);
          else apply(to);
        }

        // Dotted continents: exclusive to Digital IDs. Each dot materializes on its
        // own random schedule via uReveal (0 -> 1 in, 1 -> 0 out); the layer hides
        // once fully revealed out so Age Assurance has no dots at all.
        if (dots && dots.material && dots.material.uniforms) {
          dots.visible = true;
          const u = dots.material.uniforms;
          const sizeFrom = u.size.value;
          const revealFrom = u.uReveal.value;
          const onComplete = () => {
            if (target.dotReveal === 0) dots.visible = false;
          };
          if (animate) {
            this.startStyleTween('dotSize', sizeFrom, target.dotSize, (v) => { u.size.value = v; });
            this.startStyleTween('dotReveal', revealFrom, target.dotReveal, (v) => { u.uReveal.value = v; }, { duration: this.DOT_REVEAL_DURATION, onComplete });
          } else {
            u.size.value = target.dotSize;
            u.uReveal.value = target.dotReveal;
            onComplete();
          }
        }

        this.needsRender = true;
      }

      // Process all active transitions (called in animate loop)
,
      updateTransitions() {
        if (this.transitions.size === 0) return;
        
        const now = Date.now();
        const completed = [];
        
        this.transitions.forEach((transition, mesh) => {
          const elapsed = now - transition.startTime;
          const progress = Math.min(elapsed / transition.duration, 1);
          
          // Ease-out cubic for smooth deceleration
          const eased = 1 - Math.pow(1 - progress, 3);
          
          // Lerp color
          mesh.material.color.lerpColors(transition.startColor, transition.targetColor, eased);
          mesh.material.needsUpdate = true;
          
          if (progress >= 1) {
            completed.push(mesh);
          }
          
          this.needsRender = true;
        });
        
        // Remove completed transitions
        completed.forEach(mesh => this.transitions.delete(mesh));
      }

      // Helper to check if two meshes represent the same region (by code)
,
      isSameRegion(mesh1, mesh2) {
        if (!mesh1 || !mesh2) return false;
        return mesh1.userData?.code === mesh2.userData?.code && 
               mesh1.userData?.type === mesh2.userData?.type;
      }

,
      showTooltip(name, clientX, clientY) {
        if (!this.tooltipEl) return;
        const offset = 12;
        const track = this.tooltipTrackEl || this.tooltipEl.querySelector('.globe-region-tooltip__track');
        if (!this.tooltipTrackEl && track) this.tooltipTrackEl = track;
        const nameChanged = name !== this.lastTooltipName;
        if (nameChanged) {
          if (track) track.textContent = name || '';
          this.tooltipEl.classList.add('visible');
          this.tooltipEl.setAttribute('aria-hidden', 'false');
          this.lastTooltipName = name;
        }
        this.tooltipEl.style.left = (clientX + offset) + 'px';
        this.tooltipEl.style.top = (clientY + offset) + 'px';
        this.lastTooltipX = clientX;
        this.lastTooltipY = clientY;
      }

,
      hideTooltip() {
        if (!this.tooltipEl) return;
        this.tooltipEl.classList.remove('visible');
        this.tooltipEl.setAttribute('aria-hidden', 'true');
        this.lastTooltipName = null;
      }

,
      handleResize() {
        if (!this.container || !this.camera || !this.renderer) return;
        
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;
        
        // Guard against 0 dimensions during transitions
        if (width < 10 || height < 10) return;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
        
        // Update LineMaterial resolution for all borders
        const resolution = new THREE.Vector2(width, height);
        if (this.meshes.countryBorders) {
          this.meshes.countryBorders.forEach(mesh => {
            if (mesh.material.resolution) mesh.material.resolution.copy(resolution);
          });
        }
        if (this.meshes.stateBorders) {
          this.meshes.stateBorders.forEach(mesh => {
            if (mesh.material.resolution) mesh.material.resolution.copy(resolution);
          });
        }
        
        this.needsRender = true;
      }

,
      animate() {
        this.animationId = requestAnimationFrame(() => this.animate());

        const currentTime = Date.now();
        const deltaTime = (currentTime - this.lastTime) / 1000;
        this.lastTime = currentTime;

        // First, update globe rotation to current position
        if (this.globeGroup) {
          this.globeGroup.rotation.y = (this.rotation.x * Math.PI) / 180;
          this.globeGroup.rotation.x = -(this.rotation.y * Math.PI) / 180;
          // Update world matrices so raycast works with current rotation
          this.globeGroup.updateMatrixWorld(true);
        }

        // Run hover at most once per frame (throttled from mousemove + spin)
        if (!this.isDragging && this.lastMousePos && (this.hoverPending || this.spinSpeed > 0)) {
          this.handleHoverEffect(this.lastMousePos);
          this.hoverPending = false;
        }

        // Apply inertia when not dragging
        const hasInertia = !this.isDragging && (Math.abs(this.velocity.x) > 0.01 || Math.abs(this.velocity.y) > 0.01);
        if (hasInertia) {
          this.rotation.x += this.velocity.x;
          this.rotation.y = Math.max(-90, Math.min(90, this.rotation.y + this.velocity.y));
          
          // Natural friction model: exponential decay + small constant friction
          // This makes fast spins feel powerful while slow movements stop cleanly
          const friction = 0.005; // Small constant friction for clean stop
          this.velocity.x = this.velocity.x * this.inertiaDecay - Math.sign(this.velocity.x) * friction;
          this.velocity.y = this.velocity.y * this.inertiaDecay * 0.95 - Math.sign(this.velocity.y) * friction; // Extra damping on Y
          
          // Stop when velocity crosses zero or is negligible
          if (Math.abs(this.velocity.x) < 0.02) this.velocity.x = 0;
          if (Math.abs(this.velocity.y) < 0.02) this.velocity.y = 0;
        }

        // Pause spin when hovering over a clickable region or drawer is open
        const isHoveringClickable = this.hoveredMesh !== null;
        const isSpinning = !this.isDragging && !isHoveringClickable && !this.drawerOpen && this.spinSpeed > 0 && !hasInertia;
        const isLightOrbiting = this.keyLightOrbit;
        const hasActiveTransitions = this.transitions.size > 0;
        const hasActiveStyleTweens = this.styleTweens.size > 0;
        const shouldRender = this.needsRender || this.isDragging || isSpinning || isLightOrbiting || isHoveringClickable || hasActiveTransitions || hasActiveStyleTweens || hasInertia;

        if (!shouldRender) return;
        
        // Process smooth transitions
        this.updateTransitions();
        this.updateStyleTweens();

        // Auto-rotation when not dragging and not hovering over clickable region
        if (isSpinning) {
          this.rotation.x += this.spinSpeed * deltaTime * 10;
        }

        // Key light orbit - pause when hovering over clickable region
        if (isLightOrbiting && this.meshes.keyLight && !isHoveringClickable) {
          this.keyLightOrbitAngle += this.keyLightOrbitSpeed * deltaTime;
          // Sweep a circle derived from the resting position so enabling the
          // orbit continues from (keyLightX, keyLightY, keyLightZ) rather than
          // snapping to a different point.
          const orbitRadius = Math.hypot(this.keyLightX, this.keyLightZ);
          const orbitAngle = Math.atan2(this.keyLightZ, this.keyLightX) + this.keyLightOrbitAngle;
          this.meshes.keyLight.position.x = Math.cos(orbitAngle) * orbitRadius;
          this.meshes.keyLight.position.z = Math.sin(orbitAngle) * orbitRadius;
          this.meshes.keyLight.position.y = this.keyLightY;
          
          // Update dots shader light position
          if (this.meshes.dots && this.meshes.dots.material.uniforms) {
            this.meshes.dots.material.uniforms.lightPosition.value.copy(this.meshes.keyLight.position);
          }
        }

        // Update camera zoom
        if (this.camera) {
          this.camera.position.z = this.zoom;
        }

        this.renderer.render(this.scene, this.camera);
        this.needsRender = false;
      }

      // Pause the animation loop entirely (for view transitions)
,
      pause() {
        if (this.animationId) {
          cancelAnimationFrame(this.animationId);
          this.animationId = null;
        }
      }

      // Resume the animation loop
,
      resume() {
        if (!this.animationId) {
          this.lastTime = Date.now();
          this.needsRender = true;
          this.animate();
        }
      }

,
      hideLoadingOverlay() {
        const overlay = document.getElementById('globe-loading');
        if (!overlay || overlay.classList.contains('is-hidden')) return;
        // Cross-fade out (CSS handles the opacity transition), then remove from
        // the layout once the fade completes.
        overlay.classList.add('is-hidden');
        const remove = () => { overlay.style.display = 'none'; };
        overlay.addEventListener('transitionend', remove, { once: true });
        // Fallback in case transitionend doesn't fire (e.g. display already none).
        setTimeout(remove, 600);
      }
,
}
